import { FieldProps } from "formik";
import React, { useRef, useState } from "react";
import IconButton from "../../../IconButton";

export interface CounterInputProps {
	field?: FieldProps["field"];
	form?: FieldProps["form"];
	id: string;
	name: string;
	onKeyPress?: (e: React.KeyboardEvent) => void;
	ref?: React.RefObject<any>;
	value?: string;
	[key: string]: any;
	editInline?: boolean;
	className?: string;
	min?: number;
	max?: number;
	disabled?: boolean;
	allowZeroValue?: boolean;
	ariaName?: string;
	setShowRemainingAlert?: React.Dispatch<React.SetStateAction<boolean>>;
}

const CounterInput: React.FC<CounterInputProps> = ({
	type,
	field,
	form,
	className,
	editInline,
	id,
	name,
	min,
	max,
	disabled,
	allowZeroValue = false,
	ariaName,
	innerRef,
	setShowRemainingAlert,
	...props
}) => {
	const [disabledClickCount, setDisabledClickCount] = useState(0);
	const localMin = min && min > (allowZeroValue ? 1 : 0) ? min : 0;
	const inputRef: any = useRef(null);

	const adjustTypedInput = (val: number) => {
		if (allowZeroValue && val === 0) {
			return 0;
		} else if (val < localMin) {
			return localMin;
		} else if (max && val > max) {
			return (field && field.value?.toString()) || props.value;
		} else {
			return val;
		}
	};

	const isDecrementDisabled = () => {
		const valNumber = parseInt(field?.value);

		if (allowZeroValue && valNumber !== 0) {
			return false;
		} else if (localMin >= valNumber) {
			return true;
		} else {
			return disabled;
		}
	};

	const isIncrementDisabled = () => {
		const valNumber = parseInt(field?.value);

		if (max === 0) {
			return true;
		} else if (max && max <= valNumber) {
			return true;
		} else {
			return disabled;
		}
	};

	return (
		<div className="form-counter">
			<IconButton
				icon="minus"
				ariaLabel={`Decrement Amount for ${ariaName}`}
				classes={`minus`}
				disabled={isDecrementDisabled()}
				onClick={() => {
					if (parseInt(inputRef?.current?.value) === localMin && allowZeroValue) {
						form?.setFieldValue(field?.name || name, 0);
					} else {
						inputRef.current.stepDown();
						form?.setFieldValue(field?.name || name, inputRef.current.value);
					}

					if (setShowRemainingAlert) {
						setDisabledClickCount(0);
						setShowRemainingAlert(false);
					}
				}}
			/>
			<input
				ref={inputRef}
				id={id}
				type="number"
				{...field}
				name={(field && field.name) || name}
				value={(field && field.value?.toString()) || props.value}
				className={`${className ? className : ""}${editInline ? " edit-inline" : ""} ta-center`}
				{...props}
				min={localMin}
				max={max}
				onChange={() => {
					form?.setFieldValue(field?.name || name, adjustTypedInput(parseInt(inputRef.current.value)));
				}}
				onBlur={() => {
					if (!inputRef.current.value) {
						form?.setFieldValue(field?.name || name, 0);
					}
				}}
				disabled={disabled}
			/>
			<div className="position-relative">
				{isIncrementDisabled() && !!setShowRemainingAlert && (
					<div
						className="counter-mask"
						onClick={() => {
							const newCount = disabledClickCount + 1;
							setDisabledClickCount(newCount);

							if (newCount >= 10) {
								setShowRemainingAlert(true);
								setDisabledClickCount(0);
							}
						}}
					/>
				)}
				<IconButton
					icon="plus"
					ariaLabel={`Increment Amount for ${ariaName}`}
					classes="plus"
					disabled={isIncrementDisabled()}
					onClick={() => {
						if (parseInt(inputRef?.current?.value) === 0 && allowZeroValue && localMin !== 0) {
							form?.setFieldValue(field?.name || name, localMin);
						} else {
							inputRef.current.stepUp();
							form?.setFieldValue(field?.name || name, inputRef.current.value);
						}
					}}
				/>
			</div>
		</div>
	);
};

export default CounterInput;
